import { useState, useEffect } from 'react';
import logger from '@/utils/logger';

interface EditableTitleProps {
  value: string;
  onChange: (newValue: string) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function EditableTitle({
  value,
  onChange,
  disabled = false,
  placeholder = 'Click to edit',
  className = '',
}: EditableTitleProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);

  // Sync editedValue with value prop when it changes
  useEffect(() => {
    setEditedValue(value);
  }, [value]);

  const handleSave = async () => {
    const trimmedValue = editedValue.trim();

    // Don't save if value hasn't changed
    if (trimmedValue === value) {
      setIsEditing(false);
      return;
    }

    // Don't save if empty
    if (!trimmedValue) {
      logger.warn('EditableTitle', 'Cannot save empty title');
      setEditedValue(value); // Rollback
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    try {
      await onChange(trimmedValue);
      setIsEditing(false);
      logger.info('EditableTitle', 'Title updated successfully');
    } catch (error) {
      logger.error('EditableTitle', 'Failed to update title:', error);
      setEditedValue(value); // Rollback
      setIsEditing(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedValue(value);
    setIsEditing(false);
    logger.debug('EditableTitle', 'Edit cancelled');
  };

  return (
    <div className={className}>
      {disabled || !isEditing ? (
        <h1
          onClick={() => !disabled && setIsEditing(true)}
          className="cursor-pointer hover:bg-muted/20 px-2 rounded transition-colors"
          title={disabled ? undefined : 'Click to edit'}
        >
          {value || placeholder}
        </h1>
      ) : (
        <input
          type="text"
          value={editedValue}
          onChange={(e) => setEditedValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSave();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              handleCancel();
            }
          }}
          autoFocus
          disabled={isLoading}
          className="border border-border bg-surface text-foreground px-2 py-1 rounded w-full focus:outline-none focus:ring-2 focus:ring-primary"
          maxLength={255}
        />
      )}
    </div>
  );
}
